import React, { useState, useContext, useEffect, useRef } from 'react';
import "./checkoutForm.css";
import { useNavigate } from 'react-router-dom';
import { CheckoutContext } from './CheckoutContainer';
import ScrollToTopOnMount from './general/ScrollToTopOnMount';
import moment from 'moment-timezone';

function CheckoutForm() {
  const navigate = useNavigate();
  const { checkoutData, updateCheckoutData } = useContext(CheckoutContext);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [canChangeSchedule, setCanChangeSchedule] = useState(true);
  const [requestCallBeforeArrival, setRequestCallBeforeArrival] = useState(false);
  const [communicationPreference, setCommunicationPreference] = useState('text');
  const [additionalPersonInfo, setAdditionalPersonInfo] = useState(false);
  const [apiResponseData, setApiResponseData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState('Submitting Booking');
  const submissionTimeoutRef = useRef(null);
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6Ldl0zYsAAAAALzsrKP_U4HSYgj2GBdLrdv479fC';

  const getCookie = name => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts
        .pop()
        .split(';')
        .shift();
    }
    return 'undefined';
  };

  const setCookie = (name, value) => {
    if (typeof document === 'undefined') return;
    if (value === undefined || value === null) return;
    try {
      const encoded = encodeURIComponent(String(value));
      // Use a far-future expiration date for maximum persistence
      document.cookie = `${name}=${encoded}; expires=Fri, 31 Dec 9999 23:59:59 GMT; path=/; SameSite=Lax`;
    } catch (e) {
      // noop
    }
  };

  const getDecodedCookieOrNull = (name) => {
    const raw = getCookie(name);
    if (!raw || raw === 'undefined') return null;
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  };

  useEffect(() => {
    const existingScript = document.getElementById('google-maps-places');

    // If Maps JS already loaded (StrictMode double-mount, nav back, etc.), just init autocomplete
    if (window?.google?.maps?.places || existingScript) {
      initAutocomplete();
      return () => { };
    }

    // Otherwise create the script once
    const googleMapsScript = document.createElement('script');
    googleMapsScript.id = 'google-maps-places';
    googleMapsScript.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
    googleMapsScript.async = true;
    googleMapsScript.onload = () => initAutocomplete();
    document.body.appendChild(googleMapsScript);

    // Cleanup on unmount
    return () => {
      if (googleMapsScript.parentNode) {
        googleMapsScript.parentNode.removeChild(googleMapsScript);
      }
    };
  }, []);

  useEffect(() => {
    // Pre-fill from cookies on mount if values aren't already present
    const emailCookie = getDecodedCookieOrNull('email');
    const phoneCookie = getDecodedCookieOrNull('phoneNumber');
    const addressCookie = getDecodedCookieOrNull('address');

    if (emailCookie && !checkoutData?.email) {
      updateCheckoutData('email', emailCookie);
    }
    if (phoneCookie && !checkoutData?.phoneNumber) {
      updateCheckoutData('phoneNumber', phoneCookie);
    }
    if (addressCookie && !checkoutData?.address) {
      updateCheckoutData('address', addressCookie);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (checkoutData?.email) setCookie('email', checkoutData.email);
  }, [checkoutData?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (checkoutData?.phoneNumber) setCookie('phoneNumber', checkoutData.phoneNumber);
  }, [checkoutData?.phoneNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (checkoutData?.address) setCookie('address', checkoutData.address);
  }, [checkoutData?.address]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (submissionTimeoutRef.current) {
        clearTimeout(submissionTimeoutRef.current);
      }
    };
  }, []);

  const beginSubmission = () => {
    setSubmissionMessage('Submitting Booking');
    setIsSubmitting(true);
    if (submissionTimeoutRef.current) {
      clearTimeout(submissionTimeoutRef.current);
    }
    submissionTimeoutRef.current = setTimeout(() => {
      setSubmissionMessage('please try again');
    }, 15000);
  };

  const endSubmission = () => {
    if (submissionTimeoutRef.current) {
      clearTimeout(submissionTimeoutRef.current);
      submissionTimeoutRef.current = null;
    }
    setIsSubmitting(false);
    setSubmissionMessage('Submitting Booking');
  };


  const initAutocomplete = () => {
    const autocompleteInput = document.getElementById('autocomplete');
    if (!autocompleteInput || !window?.google?.maps?.places) {
      return;
    }
    const autocompleteOptions = {
      componentRestrictions: { country: 'us' },
      fields: ['formatted_address', 'geometry', 'address_components'],
      types: ['address'],
    };
    const autocompleteInstance = new window.google.maps.places.Autocomplete(autocompleteInput, autocompleteOptions);
    setAutocomplete(autocompleteInstance);

    let inputActive = false;

    autocompleteInstance.addListener('place_changed', () => {
      // console.log("place changed")
      const place = autocompleteInstance.getPlace();
      updateCheckoutData('address', place.formatted_address);
      updateAddressComponents(place.address_components);
    });

    autocompleteInput.addEventListener('input', (event) => {
      updateCheckoutData('address', event.target.value);
    });

    autocompleteInput.addEventListener('focus', () => {
      inputActive = true;
    });

    autocompleteInput.addEventListener('blur', () => {
      inputActive = false;

      // Only attempt to autocomplete if the input value is significantly different from the stored address
      if (!inputActive && checkoutData.address &&
        !autocompleteInput.value.includes(checkoutData.address.split(',')[0])) {
        const service = new window.google.maps.places.AutocompleteService();
        service.getPlacePredictions(
          {
            input: autocompleteInput.value,
            componentRestrictions: { country: 'us' },
            types: ['address'],
          },
          (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions.length > 0) {
              const firstPrediction = predictions[0];
              const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
              placesService.getDetails(
                { placeId: firstPrediction.place_id, fields: ['formatted_address', 'address_components'] },
                (place, status) => {
                  if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                    // Only update if the new address is significantly different
                    if (place.formatted_address !== checkoutData.address) {
                      updateAddressComponents(place.address_components);
                      updateCheckoutData('address', place.formatted_address);
                      autocompleteInput.value = place.formatted_address;
                    }
                  }
                }
              );
            }
          }
        );
      }
    });
  };

  const getRecaptchaScore = async (actionName = 'checkoutForm') => {
    // console.log('getting recaptcha score', actionName);
    if (!window.grecaptcha?.enterprise) {
      console.warn('reCAPTCHA enterprise is not available on window');
      return null;
    }
    await new Promise((resolve) => window.grecaptcha.enterprise.ready(resolve));
    const token = await window.grecaptcha.enterprise.execute(recaptchaSiteKey, { action: actionName });
    const response = await fetch('/api/createRecaptchaAssessment', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, siteKey: recaptchaSiteKey, sessionId: checkoutData.sessionId, action: actionName }),
    });

    const data = await response.json();
    const score = typeof data.score === 'number' ? data.score : null;
    // console.log(`reCAPTCHA score (${actionName}):`, score, data);
    return score;
  };

  const updateAddressComponents = (addressComponents) => {
    let zipcode = '';
    let city = '';
    let state = '';

    addressComponents.forEach((component) => {
      const types = component.types;

      if (types.includes('postal_code')) {
        zipcode = component.long_name;
      }

      if (types.includes('locality')) {
        city = component.long_name;
      }

      if (types.includes('administrative_area_level_1')) {
        state = component.short_name;
      }
    });

    if (!checkoutData.zipcode || checkoutData.zipcode !== zipcode) {
      updateCheckoutData('zipcode', zipcode);
    }
    updateCheckoutData('city', city);
    updateCheckoutData('state', state);
  };

  const addPatientName = (firstName, lastName) => {
    const trimmedFirst = firstName?.trim();
    const trimmedLast = lastName?.trim();

    if (!trimmedFirst || !trimmedLast) {
      return;
    }

    const existingNames = Array.isArray(checkoutData?.patientNames)
      ? checkoutData.patientNames
      : [];
    const alreadyExists = existingNames.some(
      (name) => name.firstName === trimmedFirst && name.lastName === trimmedLast
    );

    if (alreadyExists) {
      return;
    }

    const updatedNames = [...existingNames, { firstName: trimmedFirst, lastName: trimmedLast }];
    updateCheckoutData('patientNames', updatedNames);
  };

  // console.log(CheckoutContext);

  const handleChange = (event) => {
    const { name, value } = event.target;
    // console.log(name, value)
    updateCheckoutData(name, value);
    setFormErrors({ ...formErrors, [name]: '' });
  };

  const toggleDetails = () => {
    setDetailsVisible(!detailsVisible);
  };

  const handleBackClick = () => {
    navigate('/date');
  };

  const validateForm = () => {
    let errors = {};
    let isValid = true;

    if (!checkoutData.firstName) {
      errors.firstName = 'First name is required';
      isValid = false;
    }

    if (!checkoutData.lastName) {
      errors.lastName = 'Last name is required';
      isValid = false;
    }

    if (!checkoutData.email) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(checkoutData.email)) {
      errors.email = 'Email is invalid';
      isValid = false;
    }

    if (!checkoutData.phoneNumber) {
      errors.phoneNumber = 'Phone number is required';
      isValid = false;
    } else if (!/^\d{10}$/.test(checkoutData.phoneNumber)) {
      errors.phoneNumber = 'Phone number is invalid';
      isValid = false;
    }

    if (!checkoutData.address) {
      errors.address = 'Address is required';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const submitBookingRequest = async () => {
    const bookingNotes = checkoutData.notes ? checkoutData.notes.trim() : '';
    const scheduleChangeNote = canChangeSchedule ? 'Nurse guidance on drip requested' : '';
    const callBeforeArrivalNote = requestCallBeforeArrival ? 'Customer requested a call from a Luxe Nurse before their arrival on-site.' : '';
    const fullBookingNotes = `${bookingNotes}\n${scheduleChangeNote}\n${callBeforeArrivalNote}`.trim();

    // Extract the street address from the formatted address
    const addressParts = checkoutData.address.split(',');
    const streetAddress = addressParts[0].trim();

    const patientInfo = {
      firstName: checkoutData.firstName.trim(),
      lastName: checkoutData.lastName.trim(),
      email: checkoutData.email.trim(),
      phone: checkoutData.phoneNumber.trim(),
      address1: streetAddress,
      address2: checkoutData.aptNumber ? checkoutData.aptNumber.trim() : '',
      city: checkoutData.city,
      state: checkoutData.state,
      zipcode: checkoutData.zipcode,
      textOptIn: communicationPreference === 'text' ? true : false,
    };

    const selectedDate = moment(checkoutData.selectedDate).format('YYYY-MM-DD');
    const selectedTime = moment(checkoutData.selectedTime[0], 'hh:mm A').format('HH:mm');

    // Detect user's timezone automatically
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const startTime = moment.tz(`${selectedDate} ${selectedTime}`, 'YYYY-MM-DD HH:mm', userTimezone);

    const serviceUuid = checkoutData.selectedServiceDetails.uuid;
    const cityId = checkoutData.cityID;
    const availableStaff = checkoutData.selectedTime[1];
    const cookieInfo = {
      loggedInUserId: 0,
      source: 'websiteBooking',
      gclid: getCookie('gclid'),
      lpUrl: getCookie('lpurl'),
      ad: getCookie('ad'),
      adGroup: getCookie('ag'),
      campaign: getCookie('c'),
      keyword: getCookie('k'),
      matchType: getCookie('m'),
      gbraid: getCookie('gbraid'),
      wbraid: getCookie('wbraid'),
      timestamp: getCookie('timestamp'),
    };
    const lpUrl = (cookieInfo.lpUrl && cookieInfo.lpUrl !== 'undefined') ? cookieInfo.lpUrl : window.location.href;

    const response = await fetch('/api/createBookingWithSelectedStaff', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userInfo: patientInfo,
        startTime,
        userTimezone, // Send the detected timezone to backend
        bookingNotes: fullBookingNotes,
        serviceUuid,
        cityId,
        availableStaff,
        cookieInfo,
        lpUrl,
        sessionId: checkoutData.sessionId,
        textOptIn: communicationPreference === 'text',
      }),
    });

    const data = await response.json();
    return { response, data };
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }

    try {
      await getRecaptchaScore('completeBooking');
    } catch (err) {
      console.error('Failed to fetch reCAPTCHA score (complete booking):', err);
    }
    // console.log(checkoutData);
    // if (!validateForm()) {
    //   return;
    // }
    beginSubmission();
    try {
      const { response, data } = await submitBookingRequest();

      if (response.ok) {
        addPatientName(checkoutData.firstName, checkoutData.lastName);
        setApiResponseData(data);
        navigate('/confirmed');
        // console.log(data);
        const button = document.getElementById('submitBooking');
        // if (button) {
        //   button.classList.add('green');
        //   setTimeout(() => button.classList.remove('green'), 3000);
        // }
      } else {
        console.error('Error creating booking:', data.error);
        setErrorMessage('An error occurred while creating the booking. Please try again or contact customer support.');
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      setErrorMessage('An error occurred while creating the booking. Please try again or contact customer support.');
    } finally {
      endSubmission();
    }
  }

  async function handleSubmitAndAddPerson(e) {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }

    try {
      await getRecaptchaScore('completeBookingAddPerson');
    } catch (err) {
      console.error('Failed to fetch reCAPTCHA score (complete and add person):', err);
    }

    beginSubmission();
    try {
      const { response, data } = await submitBookingRequest();

      if (response.ok) {
        addPatientName(checkoutData.firstName, checkoutData.lastName);
        setApiResponseData(data);
        setAdditionalPersonInfo(true);
        updateCheckoutData('firstName', '');
        updateCheckoutData('lastName', '');
        const button = document.getElementById('submitBookingAddPerson');
        if (button) {
          button.classList.add('green');
          setTimeout(() => button.classList.remove('green'), 3000);

        }
      } else {
        console.error('Error creating booking:', data.error);
        setErrorMessage('An error occurred while creating the booking. Please try again or contact customer support.');
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      setErrorMessage('An error occurred while creating the booking. Please try again or contact customer support.');
    } finally {
      endSubmission();
    }
  }

  const addAdditionalPersonToDrip = async () => {
    if (isSubmitting) {
      return false;
    }

    if (!apiResponseData?.bookingInfo) {
      setErrorMessage('Unable to add another person because booking details are missing.');
      return false;
    }

    if (!checkoutData.firstName || !checkoutData.lastName) {
      setErrorMessage('Please enter a first and last name for the additional person.');
      return false;
    }

    const { bookingInfo } = apiResponseData;
    const serviceUuid = checkoutData.selectedServiceDetails?.uuid;

    if (!serviceUuid) {
      setErrorMessage('Unable to determine the selected service. Please refresh and try again.');
      return false;
    }

    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const payload = {
      userInfo: {
        firstName: checkoutData.firstName.trim(),
        lastName: checkoutData.lastName.trim(),
        email: checkoutData.email?.trim() || '',
        phone: bookingInfo.phone,
        address1: bookingInfo.address1,
        address2: bookingInfo.address2 || '',
        city: bookingInfo.city,
        state: bookingInfo.state,
        zipcode: bookingInfo.zipcode,
        textOptIn: communicationPreference === 'text',
      },
      startTime: bookingInfo.startTime,
      bookingNotes: bookingInfo.bookingNotes,
      serviceUuid,
      cityId: bookingInfo.cityCalendarId,
      staffId: bookingInfo.primaryStaffId,
      cookieInfo: bookingInfo.cookies || {},
      lpUrl: bookingInfo.lpUrl || window.location.href,
      userTimezone,
    };

    beginSubmission();
    try {
      const response = await fetch('/api/createBookingByStartTime', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        addPatientName(checkoutData.firstName, checkoutData.lastName);
        setApiResponseData(data);
        updateCheckoutData('firstName', '');
        updateCheckoutData('lastName', '');
        return true;
      } else {
        console.error('Error creating additional booking:', data.error);
        setErrorMessage('Could not add another person at this time. Please try again.');
        return false;
      }
    } catch (error) {
      console.error('Error creating additional booking:', error);
      setErrorMessage('Could not add another person at this time. Please try again.');
      return false;
    } finally {
      endSubmission();
    }
  };

  const handleFinish = async () => {
    try {
      await getRecaptchaScore('finishBooking');
    } catch (err) {
      console.error('Failed to fetch reCAPTCHA score (finish):', err);
    }

    const success = await addAdditionalPersonToDrip();
    if (success) {
      navigate('/confirmed');
    }
  };

  async function handleAddAnotherPerson(e) {
    e.preventDefault();
    try {
      await getRecaptchaScore('addAnotherPerson');
    } catch (err) {
      console.error('Failed to fetch reCAPTCHA score (add person):', err);
    }

    const success = await addAdditionalPersonToDrip();
    if (success) {
      const button = document.getElementById('addAnotherPersonButton');
      if (button) {
        button.classList.add('green');
        setTimeout(() => button.classList.remove('green'), 3000);
      }
    }
  }

  return (
    <form onSubmit={handleSubmit} className='checkout-form checkout' autoComplete="on">
      <ScrollToTopOnMount />
      <div className="layout-wrapper">
        <div className="left-section">
          <h2>Booking Details</h2>
          <div className='booking-details-subheading'>
            <p>{checkoutData.selectedServiceDetails.name}: ${checkoutData.selectedServiceDetails.price} </p>
            {/* {console.log(checkoutData)} */}
            <p>
              {checkoutData.selectedDate && moment(checkoutData.selectedDate).format('ddd. M/D/YY')} at {checkoutData.selectedTime && moment(checkoutData.selectedTime[0], 'hh:mm A').format('hh:mm A')}: {checkoutData.selectedServiceDetails.duration} Minutes
            </p>
            {/* <p>{checkoutData.selectedServiceDetails.duration} Minutes</p> */}
            {/* <p>${checkoutData.selectedServiceDetails.price}</p> */}
          </div>
          <hr />
          {/* <p style={{ marginTop: "0", fontWeight: "300" }}>Payment is collected at the time of appointment.</p> */}
          <div className="checkout-container">
            {/* 
            <div className="checkout-row input-field  one-row">
              <label htmlFor="requestCallBeforeArrival">
                <input
                  type="checkbox"
                  name="requestCallBeforeArrival"
                  id="requestCallBeforeArrival"
                  checked={requestCallBeforeArrival}
                  onChange={() => setRequestCallBeforeArrival(!requestCallBeforeArrival)}
                />
                I'd like to receive a call from a Luxe Nurse <b>before</b> they arrive on-site.
              </label>
            </div> */}
            <div className="name-row">
              <div className="checkout-row input-field name-field">
                <label htmlFor="firstName">First Name:</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={checkoutData?.firstName || ''}
                  onChange={handleChange}
                  required
                  autoComplete="given-name"
                  autoCapitalize="words"
                  autoCorrect="off"
                />
                {formErrors.firstName && <span className="error">{formErrors.firstName}</span>}
              </div>
              <div className="checkout-row input-field name-field">
                <label htmlFor="lastName">Last Name:</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={checkoutData?.lastName || ''}
                  onChange={handleChange}
                  required
                  autoComplete="family-name"
                  autoCapitalize="words"
                  autoCorrect="off"
                />
                {formErrors.lastName && <span className="error">{formErrors.lastName}</span>}
              </div>
            </div>
            {!additionalPersonInfo && (
              <>
                <div className="checkout-row input-field">
                  <label htmlFor="email">Email:</label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={checkoutData && checkoutData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                  />
                  {formErrors.email && <span className="error">{formErrors.email}</span>}
                </div>
                <div className="checkout-row input-field">
                  <label htmlFor="phoneNumber">Phone Number:</label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={checkoutData && checkoutData.phoneNumber}
                    onChange={handleChange}
                    onInput={(e) => {
                      // Remove any non-numeric characters
                      e.target.value = e.target.value.replace(/[^0-9]/g, '');
                      handleChange(e);
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    required
                    autoComplete="tel"
                  />
                  {formErrors.phoneNumber && <span className="error">{formErrors.phoneNumber}</span>}
                </div>

                <div className="checkout-row input-field">
                  <label htmlFor="autocomplete">Address:</label>
                  <input
                    type="text"
                    id="autocomplete"
                    name="street"
                    value={checkoutData && checkoutData.address}
                    onChange={handleChange}
                    required
                    // autoComplete="street-address"
                    // autoComplete="nope"
                    autoComplete="off"
                  />
                  {formErrors.address && <span className="error">{formErrors.address}</span>}
                </div>
                <div className="name-row">
                  <div className="checkout-row input-field name-field">
                    <label htmlFor="zip">Zipcode:</label>
                    <input
                      type="text"
                      name="zipcode"
                      id="zip"
                      value={checkoutData.zipcode}
                      onChange={handleChange}
                      onInput={(e) => {
                        // Remove any non-numeric characters and limit to 5 digits
                        e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 5);
                        handleChange(e);
                      }}
                      maxLength="5"
                      inputMode="numeric"
                      pattern="[0-9]{5}"
                      required
                      autoComplete="postal-code"
                    />
                    {formErrors.zipcode && <span className="error">{formErrors.zipcode}</span>}
                  </div>
                  <div className="checkout-row input-field name-field">
                    <label htmlFor="aptNumber">Apt/Suite (optional):</label>
                    <input
                      type="text"
                      id="aptNumber"
                      name="aptNumber"
                      value={checkoutData && checkoutData.aptNumber}
                      onChange={handleChange}
                      autoComplete="address-line2"
                    />
                  </div>
                </div>
              </>
            )}
            {/* {additionalPersonInfo && apiResponseData && (
              <div className="info-message">
                <p>Primary booking saved. Please enter the additional person&apos;s information.</p>
              </div>
            )} */}
            <div className="checkout-row input-field">
              <label htmlFor="notes">Other Notes (Gate code, etc):</label>
              <textarea id="notes" name="notes" value={checkoutData && checkoutData.notes} onChange={handleChange} />
            </div>

            {!additionalPersonInfo && (
              <div className="checkout-row input-field one-row" style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Communication Preference:
                </label>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="radio"
                      name="communicationPreference"
                      value="text"
                      checked={communicationPreference === 'text'}
                      onChange={(e) => setCommunicationPreference(e.target.value)}
                    />
                    Text*
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="radio"
                      name="communicationPreference"
                      value="call"
                      checked={communicationPreference === 'call'}
                      onChange={(e) => setCommunicationPreference(e.target.value)}
                    />
                    Call
                  </label>
                </div>
                <div className="checkout-row input-field one-row" style={{ marginTop: '1rem' }}>
                  <label htmlFor="canChangeSchedule">
                    <input
                      type="checkbox"
                      name="canChangeSchedule"
                      id="canChangeSchedule"
                      checked={canChangeSchedule}
                      onChange={() => setCanChangeSchedule(!canChangeSchedule)}
                    />
                    <b>I'm not sure I chose the right drip. </b> I'll talk with the nurse when they arrive.(FREE)
                  </label>
                </div>
              </div>

            )}

          </div>
          {errorMessage && (
            <div className="error-message">
              <p>{errorMessage}</p>
            </div>
          )}
          <div style={{ width: "100%", textAlign: "left" }}>
            <br />
            {!additionalPersonInfo && (
              <>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <button
                    id="submitBooking"
                    type="submit"
                    className='checkout-navigation-button'
                    style={{ margin: "auto" }}
                    disabled={isSubmitting}
                  >
                    Complete <br /> Booking
                  </button>
                  <button
                    id="submitBookingAddPerson"
                    type="button"
                    className='checkout-navigation-button secondary'
                    style={{ margin: "auto" }}
                    onClick={handleSubmitAndAddPerson}
                    disabled={isSubmitting}
                  >
                    Add Additional <br /> Person
                  </button>
                </div>
                <br /><br />
              </>
            )}
            {additionalPersonInfo && (
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <button
                  id="addAnotherPersonButton"
                  type="button"
                  className='checkout-navigation-button secondary'
                  style={{ margin: "auto" }}
                  onClick={handleAddAnotherPerson}
                  disabled={isSubmitting}
                >
                  Add Another Person
                </button>
                <button
                  type="button"
                  className='checkout-navigation-button'
                  style={{ margin: "auto" }}
                  onClick={handleFinish}
                  disabled={isSubmitting}
                >
                  Complete <br /> Booking
                </button>
              </div>
            )}
            {/* <button className='checkout-navigation-button' style={{ margin: "auto" }} onClick={handleBackClick}>Back</button> */}
          </div>
          {!additionalPersonInfo && (
            <div style={{ width: "100%", textAlign: "left", fontSize: '0.8rem', color: '#666' }}>
              * A required statement by law for texting: By submitting this form, you consent to receive appointment updates from Luxe Mobile IV. Consent is not a condition of purchase. Message & data rates may apply. Message frequency varies. Unsubscribe by replying STOP. Reply HELP for help. Phone numbers aren't shared with third parties. <a href="/luxe-mobile-iv-privacy-policy.html" target="_blank">Privacy Policy</a> & <a href="/luxe-mobile-iv-legal-policy.html" target="_blank">Terms</a>.
            </div>
          )}
        </div>
        {/* <div className="right-section-final"> */}

        {/* </div> */}
      </div>
      {isSubmitting && (
        <div className="submission-overlay" role="alert" aria-live="assertive">
          <div className="submission-modal">
            <p>{submissionMessage}</p>
          </div>
        </div>
      )}
    </form>
  );
}

export default CheckoutForm;